'use client';

import { useObservable } from '@legendapp/state/react';
import { type ReactNode, createContext, use } from 'react';
import { type Observable, batch } from '@legendapp/state';

interface ExpandableGridState {
    active: boolean;
    expandedIds: string[];
    overlappedIds: string[];
    lastExpandedId?: string;
    isExpanded: (id: string) => boolean;
    isOverlapped: (id: string) => boolean;
    isLast: (id: string) => boolean;
    toggleExpanded: (id: string) => void;
}

export const ExpandableGridContext = createContext<Observable<ExpandableGridState>>(null as unknown as Observable<ExpandableGridState>);

export interface ExpandableGridProviderProps {
    options?: Partial<ExpandableGridState>;
    children: ReactNode;
}

export function ExpandableGridProvider({ children, options: { active = true, ...options } = {} }: ExpandableGridProviderProps) {
    const expandableGridState$: Observable<ExpandableGridState> = useObservable<ExpandableGridState>({
        active,
        expandedIds: [],
        overlappedIds: [],
        isExpanded: (id: string) => expandableGridState$.expandedIds.get().includes(id),
        isOverlapped: (id: string) => expandableGridState$.overlappedIds.get().includes(id),
        isLast: (id: string) => expandableGridState$.lastExpandedId.get() === id,
        toggleExpanded: (id: string) => {
            const expandedIds = expandableGridState$.expandedIds.get();
            const overlappedIds = expandableGridState$.overlappedIds.get();

            const shouldExpand = !expandedIds.includes(id);
            const filterCurrent = (str: string) => str !== id;

            if (shouldExpand) {
                batch(() => {
                    expandableGridState$.overlappedIds.set([id, ...overlappedIds]);
                    expandableGridState$.expandedIds.set([id, ...expandedIds]);
                    expandableGridState$.lastExpandedId.set(id);
                });
            } else {
                expandableGridState$.expandedIds.set(expandedIds.filter(filterCurrent));
                setTimeout(() => {
                    expandableGridState$.overlappedIds.set(overlappedIds.filter(filterCurrent));
                }, 300);
            }
        },
        ...options,
    });
    return <ExpandableGridContext value={expandableGridState$}>{children}</ExpandableGridContext>;
}

export function useExpandableGridState() {
    const expandableGridState$ = use(ExpandableGridContext);
    if (!expandableGridState$) {
        throw new Error('useExpandableGridState must be used within a ExpandableGridProvider');
    }
    return expandableGridState$;
}
